import { prettyError } from '@grin-rnd/grin-api-sdk/dist/utils/ErrorUtils'
import { NOTIFICATION_CENTER_PAGE_SIZE } from 'consts/notificationCenterConsts'
import * as notificationCenterGraphql from 'graphql/notificationCenter.graphql'
import { api, REQUEST_TYPE } from 'store/api'
import { logError } from 'utils/logUtils'
import { apiEndpointProfiler, patchPaginatedGraphQueryState } from 'utils/rtkUtils'
import { ModuleTags } from './consts'
import { buildGrinNotificationFilter } from 'utils/notificationCenterUtils'
import { GrinNotificationStatuses } from '@grin-rnd/grin-api-sdk/dist/Enums/Notifications'

export const NotificationCenter = api.injectEndpoints({
  endpoints: build => ({
    fetchTotalUnreadNotifications: build.query({
      query: () => ({
        type: REQUEST_TYPE.GET,
        path: '/notifications/v1/grinNotifications/aggregation'
      }),
      providesTags: [ModuleTags.UnreadNotifications],
      onQueryStarted: async (args, { queryFulfilled }) => {
        await apiEndpointProfiler({
          endpointName: 'fetchTotalUnreadNotifications',
          queryFulfilled
        })
      }
    }),
    fetchGrinNotifications: build.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages, lastPageParam) => lastPageParam + 1
      },
      keepUnusedDataFor: 0,
      providesTags: [ModuleTags.GrinNotificationsList],
      invalidatesTags: [ModuleTags.UnreadNotifications],
      query: ({ queryArg, pageParam }) => {
        const { grinUserId, filters } = queryArg
        return {
          type: REQUEST_TYPE.GRAPHQL,
          query: notificationCenterGraphql.searchGrinNotifications(),
          queryName: 'searchGrinNotifications',
          variables: {
            limit: NOTIFICATION_CENTER_PAGE_SIZE,
            from: pageParam * NOTIFICATION_CENTER_PAGE_SIZE,
            sort: {
              field: 'eventDate',
              direction: 'desc'
            },
            filter: buildGrinNotificationFilter({
              grinUserId: grinUserId,
              relatedAccountOwnerId: filters.practiceId,
              unreadOnly: filters.unreadOnly,
              notificationTypes: filters.notificationTypes,
              lastMarkAllAsReadDate: filters.lastMarkAllAsReadDate,
              lastClearAllDate: filters.lastClearAllDate
            })
          }
        }
      },
      transformResponse: res => ({
        items: res.searchGrinNotifications.items.map(notification => ({
          ...notification,
          payload: JSON.parse(notification.payload || '{}')
        })),
        hasMore: !!res.searchGrinNotifications.nextToken
      }),
      onQueryStarted: async (args, { queryFulfilled }) => {
        await apiEndpointProfiler({
          endpointName: 'fetchGrinNotifications',
          queryFulfilled,
          extraParams: args
        })
      }
    }),
    setGrinNotificationStatus: build.mutation({
      query: ({ grinNotificationId, status }) => ({
        type: REQUEST_TYPE.GRAPHQL_MUTATION,
        entityType: 'GrinNotification',
        id: grinNotificationId,
        variables: {
          status,
          resolveMethod: 'user-action',
          lastStatusUpdateDate: new Date().toISOString()
        }
      }),
      onQueryStarted: async ({ grinNotificationId, status }, { dispatch, queryFulfilled, getState }) => {
        const state = getState()
        const fetchGrinNotificationsArgs = {
          filters: state.notificationCenterReducer.filters,
          grinUserId: state.profileReducer.doctor?.user?.id
        }

        const fetchGrinNotificationsPatch = dispatch(
          api.util.updateQueryData('fetchGrinNotifications', fetchGrinNotificationsArgs, notifications => {
            patchPaginatedGraphQueryState({
              pages: notifications.pages,
              predicate: item => item.id === grinNotificationId,
              itemPatch: {
                status,
                lastStatusUpdateDate: new Date().toISOString()
              }
            })
          })
        )

        const fetchTotalUnreadNotificationsPatch = dispatch(
          api.util.updateQueryData('fetchTotalUnreadNotifications', null, totalUnreadNotifications => {
            const delta = status === GrinNotificationStatuses.New ? 1 : -1
            totalUnreadNotifications.total += delta
          })
        )

        try {
          await queryFulfilled
        } catch (error) {
          fetchGrinNotificationsPatch.undo()
          fetchTotalUnreadNotificationsPatch.undo()
          logError('setGrinNotificationStatus: An error occured while trying to update a GrinNotification status', {
            grinNotificationId,
            status,
            error: prettyError(error)
          })
        }
      }
    }),
    bulkAction: build.mutation({
      query: ({ actionType, filters }) => ({
        type: REQUEST_TYPE.POST,
        path: `/notifications/v1/grinNotifications/bulkAction/${actionType}`
      }),
      invalidatesTags: [ModuleTags.GrinNotificationsList, ModuleTags.UnreadNotifications]
    })
  })
})

export const {
  useFetchTotalUnreadNotificationsQuery,
  useFetchGrinNotificationsInfiniteQuery,
  useSetGrinNotificationStatusMutation,
  useBulkActionMutation
} = NotificationCenter

export const { bulkAction, fetchTotalUnreadNotifications } = NotificationCenter.endpoints
